import { injectGlobal } from "styled-components"

const time = {
    xl: 1666,
    l: 1000,
    m: 600,
    s: 150
}

const ease = {
    enter: {
        l: "cubic-bezier(0.215, 0.61, 0.355, 1)" /* easeOutCubic */,
        m: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" /* easeOutQuad */,
        s: "cubic-bezier(0.39, 0.575, 0.565, 1)" /* easeOutSine */
    },
    move: {
        l: "cubic-bezier(0.645, 0.045, 0.355, 1)" /* easeInOutCubic */,
        m: "cubic-bezier(0.455, 0.03, 0.515, 0.955)" /* easeInOutQuad */,
        s: "cubic-bezier(0.445, 0.05, 0.55, 0.95)" /* easeInOutSine */
    },
    exit: {
        l: "cubic-bezier(0.55, 0.055, 0.675, 0.19)" /* easeInCubic */,
        m: "cubic-bezier(0.645, 0.045, 0.355, 1)" /* easeInOutCubic */,
        s: "cubic-bezier(0.47, 0, 0.745, 0.715)" /* easeInSine */
    }
}

const theme = {
    color: {
        primary: "#4ebed7", // Blue
        secondary: "#d74ebe", // Purple
        success: "#bed74e", // Green
        alert: "#fde74c", // Yellow
        warning: "#d7674e", // Red
        focus: "#ffffff", // White
        line: "hsla(360, 100%, 100%, 0.3)", // Semi transparent white
        primaryLight: "#82d1e3", // Hover version of primary

        background: {
            l: "#435065", // Brightest (hover button backgrounds)
            m: "#344156", // Medium-light (standard button background)
            s: "#293547", // Medium-dark (standard body background)
            xs: "#171f2b", // Dark (cut-out background)
            xsTransparent: "hsla(217, 31%, 13%, 0.7)" // Dark (semi-transparent for some modals)
        }
    },

    font: {
        // http://www.modularscale.com/?1&em&1.333
        size: {
            xxxxl: "6rem",
            xxxl: "3.157rem",
            xxl: "2.369rem",
            xl: "1.777rem",
            l: "1.333rem",
            m: "1rem",
            s: "0.8rem"
        },
        family: {
            heading: "'Poppins Light', sans-serif",
            reading: "'Poppins Light', 'Roboto', sans-serif"
        },

        // https://material.io/guidelines/style/color.html#color-usability
        color: {
            l: "hsla(360, 100%, 100%, 1)",
            m: "hsla(360, 100%, 100%, 0.7)",
            s: "hsla(360, 100%, 100%, 0.3)",
            xs: "hsla(360, 100%, 0%, 0.8)"
        },

        space: {
            l: "0.1rem",
            m: "0.05rem",
            xs: ".7px"
        }
    },

    space: {
        xl: "8rem",
        l: "4rem",
        m: "1.8rem",
        s: "0.7rem",
        xs: "0.3rem",
        none: "0px",
        fixedSix: "6px",
        icon: "1.2rem"
    },

    opacity: {
        l: 0.8,
        m: 0.5,
        s: 0.3,
        disable: 0.3
    },

    // moved all shadows up a lvl as small shadows are hard to see on dark blue background
    boxShadow: {
        xl: "0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)",
        l: "0 10px 20px  rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
        m: "0 3px 6px    rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
        s: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",

        shadowUp: {
            /* use for bottom aligned elements,  when you want the shadow above the element*/
            xl: "0 -19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)",
            l: "0 -10px 20px  rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
            m: "0 -3px 6px    rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
            s: "0 -1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)"
        },
        inset: {
            xl: "inset 0 19px 38px rgba(0,0,0,0.30), inset 0 15px 12px rgba(0,0,0,0.22)",
            l: "inset 0 10px 20px  rgba(0,0,0,0.19), inset 0 6px 6px rgba(0,0,0,0.23)",
            m: "inset 0 3px 6px    rgba(0,0,0,0.16), inset 0 3px 6px rgba(0,0,0,0.23)",
            s: "inset 0 1px 3px rgba(0,0,0,0.12), inset 0 1px 2px rgba(0,0,0,0.24)"
        }
    },

    dropShadow: {
        m: "drop-shadow(0 6px 24px hsla(0, 0%, 0%, 0.75))",
        s: "drop-shadow(0 3px 8px hsla(0, 0%, 0%, 0.75))",
        xs: "drop-shadow(0 1px 2px hsla(0, 0%, 0%, 0.55))"
    },

    scale: {
        l: 2,
        m: 1.2,
        s: 1.04
    },

    size: {
        icon: {
            m: "6rem"
        }
    },
    buttonRadius: "3px",
    time: { ...time },
    ease: { ...ease },
    transition: {
        enter: {
            l: `all ${time.l}ms ${ease.enter.l}`,
            m: `all ${time.m}ms ${ease.enter.m}`,
            s: `all ${time.s}ms ${ease.enter.s}`
        },
        move: {
            l: `all ${time.l}ms ${ease.move.l}`,
            m: `all ${time.m}ms ${ease.move.m}`,
            s: `all ${time.s}ms ${ease.move.s}`
        },
        exit: {
            l: `all ${time.l}ms ${ease.exit.l}`,
            m: `all ${time.m}ms ${ease.exit.m}`,
            s: `all ${time.s}ms ${ease.exit.s}`
        }
    }
}

// add global styles
injectGlobal`
  @import url('https://fonts.googleapis.com/css?family=Open+Sans:300,400|Roboto:300,400|Poppins:300,400|Roboto+Mono:300,400');

  * {
    margin: 0;
	  padding: 0;
	  border: none;
    box-sizing: border-box;
    vertical-align: baseline;
	  font-size: 100%;
    font-family: 'Roboto', sans-serif;
    font-weight: inherit;
  }
  /* font weight being ignored in chrome*/
  * {-webkit-font-smoothing: antialiased;}

  html {
    color: ${theme.font.color.m};
    font-weight: 300;
    background-color: ${theme.color.background.xs};

  ${"" /* setting rem values */}
    font-size: 16px;
    @media (max-width: 900px) { font-size: 13px; }
    @media (max-width: 600px) { font-size: 12px; }
  }


  html,
  body,
  #root {
    width: 100%;
    min-height: 100%;
    display: flex;
    overflow-x: hidden;
  }

  #root {
    padding: 0 ${theme.space.s};
    flex-direction: column;
    &>*:not(:last-child) { margin-bottom: ${theme.space.m}; }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    /* remove pixels below display: inline */
    display: inline-block;
    font-family:  'Poppins Light', sans-serif;
  }

  p {
    /* You should have around 60 characters per line if you want a good reading experience */
    max-width: calc(11 * 48px);
    letter-spacing: 0.2px;
    /* line-height: 1.3em; */
  }

  ul,
  ol {
    list-style: none;
  }

  iframe {
    /* remove pixels below display: inline */
    display: block;
    /* so imgs will resize based on wrapper constraints */
    ${"" /* max-width: 100%; */}
    /* prevent image from stretching due to 'align-self: default' value is stretch (in flexbox context) */
    align-self: center;
  }

  a {
    text-decoration: none;
    color: inherit;
    outline: none;
  }

  p {
    a {
      padding: 0;
      display: inline-flex;
      color: ${theme.color.primary};
    }
  }

  b {
    font-weight: bold;
  }

  img {
    max-width: 100%;
    /* remove pixels below display: inline */
    display: block;
    /* prevent image from stretching due to 'align-self: default' value is stretch (in flexbox context) */
    align-self: center;
  }

  button {
    padding: ${theme.space.s};
    /* remove pixels below display: inline */
    display: inline-flex;
    justify-content: center;
    align-items: center;

  ${"" /* color was being set to black */}
    color: inherit;
  ${"" /* material-UI was adding default border-radius */}
    border-radius: 0;
    cursor: pointer;
    background: none;

  ${"" /* if using this, ensure to use an alternative for disability software */}
    :focus {
      outline: none;
    }
  }

  input,
  textarea {
    cursor: pointer;
  }

  input {
    color: inherit;
    background-color: transparent;
  }

  textarea {
    resize: vertical;
  }

  svg {
    width: 100%;
  }

  .readingText {
    font-family: 'Poppins Light', 'Roboto', sans-serif;
    line-height: 1.4em;
  }
  .primaryLink {
    color:  ${theme.color.primary};
  }
  
  [disabled] {
    cursor: pointer;
    pointer-events: none;
    opacity: 0.3;
  }
  a[disabled] {
    cursor: pointer;
    pointer-events: none;
    color:  ${theme.color.primary};
    opacity: 1;
  }

  input,
  textarea,
  select,
  input:hover,
  textarea:hover,
  select:hover,
  input:focus,
  textarea:focus,
  select:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus {
    -webkit-animation: autofill 0s forwards;
    color: ${theme.font.color.m};
    animation: autofill 0s forwards;

    ${"" /* if using this, ensure to use an alternative for disability software */}
    outline: none;

    ::placeholder {
      color: ${theme.font.color.s};
    }
  }

  @keyframes autofill {
    100% {
        background: ${theme.color.background.m};
        color: inherit;
    }
  }

  @-webkit-keyframes autofill {
      100% {
          background: ${theme.color.background.m};
          color: inherit;
      }
  }

.theme-background-s {
  background-color: ${theme.color.background.s} ;
}
.theme-background-m {
  background-color: ${theme.color.background.m};
}
.theme-background-l {
  background-color: ${theme.color.background.l};
}
.theme-background-xs {
  background-color: ${theme.color.background.xs};
}
.theme-background-xs-transparent {
  background-color: ${theme.color.background.xsTransparent};
}
.theme-padding-s {
  padding: ${theme.space.s};
}
.theme-padding-m {
  padding: ${theme.space.m};
}
.theme-padding-l {
  padding: ${theme.space.l};
}
.theme-padding-xl {
  padding: ${theme.space.xl};
}
.theme-padding-six {
  padding: ${theme.space.fixedSix};
}

.theme-margin-s {
  margin: ${theme.space.s};
}
.theme-margin-m {
  margin: ${theme.space.m};
}
.theme-margin-l {
  margin: ${theme.space.l};
}
.theme-margin-xl {
  margin: ${theme.space.xl};
}
.theme-margin-six {
  margin: ${theme.space.fixedSix};
}


${"" /* material design icons ---------------------------------------------------------- */}
${"" /* http://google.github.io/material-design-icons/#getting-icons */}
  .material-icons.md-18 { font-size: 18px; }
  .material-icons.md-24 { font-size: 24px; }
  .material-icons.md-36 { font-size: 36px; }
  .material-icons.md-48 { font-size: 48px; }

  /* Rules for using icons as black on a light background. */
  .material-icons.md-dark { color: rgba(0, 0, 0, 0.54); }
  .material-icons.md-dark.md-inactive { color: rgba(0, 0, 0, 0.26); }

  /* Rules for using icons as white on a dark background. */
  .material-icons.md-light { color: rgba(255, 255, 255, 1); }
  .material-icons.md-light.md-inactive { color: rgba(255, 255, 255, 0.3); }

  /* Rules for LinearProgress */
  .MuiLinearProgress-colorPrimary	{
    background-color: ${theme.color.line} !important
  }
  .MuiLinearProgress-indeterminate {
    background-color: rgba(0, 0, 0, 0) !important

  }
  .MuiLinearProgress-barColorPrimary	{
    background-color: ${theme.color.primary} !important
  }

`

export default theme



// WEBPACK FOOTER //
// ./src/theme.js